import React, { useEffect, useRef, useState } from 'react';
import styles from '../styles/Visualization.module.css';
import { Scene } from './three/Scene';
import { GodRays } from './three/GodRays';
import * as THREE from 'three';
import { init as initRaw, dispose as disposeRaw, setSunColor, setBackgroundColor, setGodrayIntensity } from './three/raw';

interface VisualizationProps extends React.HTMLAttributes<HTMLDivElement> {
  children?: React.ReactNode;
  audioAnalyser?: AnalyserNode;
  backgroundColor?: string;
  customCode?: string;
  mode?: 'default' | '3d' | 'godrays' | 'raw';
  lightColor?: number;
  lightIntensity?: number;
  sunColor?: number;
}

interface SceneParams {
  moveSpeed: number;
  rippleSpeed: number;
  rippleIntensity: number;
  glowIntensity: number;
  waterOpacity: number;
  fogDensity: number;
}

export function Visualization({ 
  audioAnalyser,
  backgroundColor='#000000',
  customCode,
  mode = 'raw',
  className,
  style,
  onStartChat,
  lightColor,
  lightIntensity,
  sunColor,
  children,
  ...props
}: VisualizationProps & { onStartChat?: () => void }): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sceneRef = useRef<Scene | null>(null);
  const godraysRef = useRef<GodRays | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const [showOverlay, setShowOverlay] = useState(true);
  const [isShakeButtonDisabled, setIsShakeButtonDisabled] = useState(false);
  const [localSunColor, setLocalSunColor] = useState('#ffee00');
  const [localBgColor, setLocalBgColor] = useState('#000511');
  const [localGodrayIntensity, setLocalGodrayIntensity] = useState(0.75);
  const smoothedIntensityRef = useRef(0.75);
  const initialMount = useRef(true);
  
  // Audio visualization parameters
  const [audioParams, setAudioParams] = useState({
    godrayIntensityMin: 0.7,
    godrayIntensityMax: 0.1,
    smoothingFactor: 0.1  // Lower = smoother (0-1)
  });
  const [sceneParams, setSceneParams] = useState<SceneParams>({
    moveSpeed: 0.8,
    rippleSpeed: 12.0,
    rippleIntensity: 1.5,
    glowIntensity: 3.0,
    waterOpacity: 0.85,
    fogDensity: 0.003
  });
  
  // Initialize visualization based on mode
  useEffect(() => {
    if (!containerRef.current) return;

    // Ensure container has proper size
    containerRef.current.style.width = '100%';
    containerRef.current.style.height = '100%';
    
    if (mode === '3d' && !sceneRef.current) {
      sceneRef.current = new Scene();
      sceneRef.current.params = { ...sceneParams };
      if (sceneRef.current.renderer) {
        containerRef.current.appendChild(sceneRef.current.renderer.domElement);
      }
    } else if (mode === 'godrays' && !godraysRef.current) {
      godraysRef.current = new GodRays(containerRef.current);
      if (lightColor !== undefined) {
        godraysRef.current.setLightColor(lightColor);
      }
      if (lightIntensity !== undefined) {
        godraysRef.current.setLightIntensity(lightIntensity);
      }
    } else if (mode === 'raw' && containerRef.current) {
      try {
        initRaw(containerRef.current);
        if (sunColor !== undefined) {
          setSunColor(sunColor);
        }
      } catch (error) {
        console.error('Error initializing raw mode:', error);
      }
    }
  }, [mode, sunColor]);

  // Handle audio visualization
  useEffect(() => {
    if (!audioAnalyser || (!sceneRef.current && mode !== 'raw')) return;
    
    const dataArray = new Uint8Array(audioAnalyser.frequencyBinCount);
    
    const processAudio = () => {
      animationFrameRef.current = requestAnimationFrame(processAudio);
      audioAnalyser.getByteFrequencyData(dataArray);
      
      // Calculate average intensity
      const avgIntensity = dataArray.reduce((a, b) => a + b) / dataArray.length;
      const normalizedIntensity = avgIntensity / 255;

      // Update godray intensity in raw mode
      if (mode === 'raw') {
        if (normalizedIntensity > 0.05) { // If there is significant audio
          // Map normalized intensity (0-1) to godray intensity range
          const targetIntensity = audioParams.godrayIntensityMin + 
            (normalizedIntensity * (audioParams.godrayIntensityMax - audioParams.godrayIntensityMin));
          
          // Smooth the transition
          smoothedIntensityRef.current += (targetIntensity - smoothedIntensityRef.current) * audioParams.smoothingFactor;
        } else {
          // Return to default intensity when no significant audio
          smoothedIntensityRef.current += (0.75 - smoothedIntensityRef.current) * audioParams.smoothingFactor;
        }
        
        setGodrayIntensity(smoothedIntensityRef.current);
        setLocalGodrayIntensity(smoothedIntensityRef.current);
      }
      
      // Update 3D scene if available
      if (mode === '3d' && sceneRef.current) {
        
        if (avgIntensity > 200) {
          sceneRef.current.startShake(normalizedIntensity, 200, normalizedIntensity * 8);
        }
        
        if (sceneRef.current.grid && sceneRef.current.params) {
          sceneRef.current.params.rippleIntensity = 0.5 + normalizedIntensity;
        }
        
        if (sceneRef.current.figure?.mesh?.material?.uniforms) {
          if (!sceneRef.current.figure.mesh.material.uniforms.audioIntensity) {
            sceneRef.current.figure.mesh.material.uniforms.audioIntensity = { value: 0 };
          }
          sceneRef.current.figure.mesh.material.uniforms.audioIntensity.value = normalizedIntensity;
        }
      }
    };
    
    processAudio();
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [mode, audioAnalyser]);

  // Handle visualization updates
  useEffect(() => {
    if (mode === 'raw') {
        // Only affect after initial mount
        if (initialMount.current) {
          initialMount.current = false;
          return;
        }

        const newColor = backgroundColor || localSunColor;
        setLocalSunColor(newColor);
        const colorNum = parseInt(newColor.substring(1), 16);
        setSunColor(colorNum);
      }

    const currentScene = mode === '3d' ? sceneRef.current : godraysRef.current;
    if (!currentScene) return;
    
    currentScene.renderer?.setClearColor(backgroundColor);


    
    if (customCode && mode === '3d' && sceneRef.current) {
      try {
        const customFunction = new Function('scene', 'THREE', customCode);
        customFunction(sceneRef.current, THREE);
      } catch (error) {
        console.error('Error in custom visualization code:', error);
      }
    }
  }, [mode, backgroundColor, customCode]);

  // Handle default audio visualization
  useEffect(() => {
    if (!canvasRef.current || !audioAnalyser) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.clientWidth;
      canvas.height = canvas.clientHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const dataArray = new Uint8Array(audioAnalyser.frequencyBinCount);
    
    const draw = () => {
      animationFrameRef.current = requestAnimationFrame(draw);
      
      audioAnalyser.getByteFrequencyData(dataArray);
      
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      const barWidth = (canvas.width / dataArray.length) * 2.5;
      let x = 0;
      
      for (let i = 0; i < dataArray.length; i++) {
        const barHeight = (dataArray[i] / 255) * canvas.height;
        
        const gradient = ctx.createLinearGradient(0, canvas.height - barHeight, 0, canvas.height);
        gradient.addColorStop(0, '#ffffff');
        gradient.addColorStop(1, backgroundColor);
        
        ctx.fillStyle = gradient;
        ctx.fillRect(x, canvas.height - barHeight, barWidth, barHeight);
        
        x += barWidth + 1;
      }

      // Send audio data to iframe
      if (iframeRef.current?.contentWindow) {
        iframeRef.current.contentWindow.postMessage({
          type: 'audioData',
          data: Array.from(dataArray)
        }, '*');
      }
    };
    
    draw();

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      window.removeEventListener('resize', resize);
    };
  }, [audioAnalyser, backgroundColor]);

  // Handle custom visualization
  useEffect(() => {
    if (!iframeRef.current || !customCode) return;

    const iframe = iframeRef.current;
    const doc = iframe.contentDocument;
    if (!doc) return;

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            body { 
              margin: 0; 
              overflow: hidden; 
              background-color: ${backgroundColor};
            }
          </style>
        </head>
        <body>
          ${customCode}
          <script>
            window.addEventListener('message', (event) => {
              if (event.source === window.parent) {
                window.audioData = event.data.data;
              }
            });
          </script>
        </body>
      </html>
    `;

    doc.open();
    doc.write(html);
    doc.close();
  }, [customCode, backgroundColor]);

  const handleShake = () => {
    if ((!sceneRef.current && !godraysRef.current) || isShakeButtonDisabled) return;
    
    if (mode === '3d' && sceneRef.current) {
      sceneRef.current.startShake(0.5, 200, 30.0);
    }
    
    setIsShakeButtonDisabled(true);
    setTimeout(() => setIsShakeButtonDisabled(false), 1000);
  };

  const handleStartChat = () => {
    setShowOverlay(false);
    
    setTimeout(() => {
      if (mode === '3d' && sceneRef.current) {
        sceneRef.current.startAutoMove(() => {
          console.log('Camera movement completed');
          if (onStartChat) onStartChat();
        });
      } else if (onStartChat) {
        onStartChat();
      }
    }, 800);
  };

  return (
    <div className={`${styles.container} ${className || ''}`} style={style} {...props}>
      {children}
      {mode === 'default' ? (
        <>
          <canvas 
            ref={canvasRef}
            className={styles.canvas}
            style={{ backgroundColor }}
          />
          <iframe
            ref={iframeRef}
            className={styles.iframe}
            sandbox="allow-scripts allow-same-origin"
          />
        </>
      ) : (
        <div ref={containerRef} className={styles.canvas} style={{ width: '100%', height: '100%' }} />
      )}
    </div>
  );
}
